import { UseFormReset } from "react-hook-form";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useGlobalManagerApproval } from "@mibuildweb/components";
import { useCategories, useDropTypes, usePriceLevels } from "@mibuildweb/hooks";
import {
  ICategoryDropPayload,
  IPriceLevelPayload,
} from "./PricingMatrix.types";

export const usePricingMatrixViewModel = ({
  reset = () => {},
}: usePricingMatrixViewModelProps) => {
  const {
    isLoading: isCategoriesLoading,
    categories,
    categoryDrops,
    getCategories,
    getCategoryDrops,
    createCategoryDrop,
    getDeleteCategoryDropOptions,
  } = useCategories();
  const {
    isLoading: isDropTypesLoading,
    dropTypes,
    getDropTypes,
  } = useDropTypes();
  const {
    isLoading: isPriceLevelLoading,
    priceLevels,
    getPriceLevels,
    createPriceLevel,
    updatePriceLevel,
    reorderPriceLevels,
    duplicatePriceLevel,
    getDeletePriceLevelOptions,
  } = usePriceLevels();
  const { executeApiRequest } = useGlobalManagerApproval();

  const handleCreateCategoryDrop = async (
    drops: string[],
    categoryId: string
  ) => {
    const promises = drops.map(async (dropId) => {
      const dropType = dropTypes.find((dt) => dt.id === dropId);
      if (!dropType) return false;
      const data = { name: dropType.name, dropTypeId: dropType.id };
      const response = await createCategoryDrop(data, categoryId);
      return response.status === HTTP_STATUSES.OK;
    });
    const results = await Promise.all(promises);
    if (results.some((success) => success)) {
      getCategoryDrops();
    }
  };

  const handleDeleteCategoryDrop = async (
    categoryId: string,
    data: Partial<ICategoryDropPayload>
  ) => {
    const options = await getDeleteCategoryDropOptions(categoryId, data);
    await executeApiRequest({
      options,
      callback: (res: { status: number }) => {
        if (res.status === HTTP_STATUSES.OK) {
          getCategoryDrops();
          getPriceLevels();
        }
      },
      noInitialErrorMessage: true,
      modalDescription:
        "Provide a reason for this deletion. A manager must enter their pin below to approve and continue.",
      confirmBtnLabel: "Approve",
      successToastMessageForManagerApproval: {
        text: `Drop ${data.name} deleted successfully.`,
      },
    });
  };

  const handleCreatePriceLevel = async (data: IPriceLevelPayload) => {
    const modifiedData = { ...data, displayOrder: priceLevels.length };
    const response = await createPriceLevel(modifiedData);
    if (response.status === HTTP_STATUSES.OK) {
      const dropPrices = categoryDrops.map((cd) => ({
        dropTypeId: cd.dropTypeId,
        categoryId: cd.categoryId,
        dropId: cd.id,
      }));
      reset({ ...response.data, dropPrices });
      getPriceLevels();
    }
  };

  const handleUpdatePriceLevel = async (
    data: Partial<IPriceLevelPayload>,
    successText?: string
  ) => {
    const modifiedData = {
      ...data,
      dropPrices: data.dropPrices?.filter((dp) => dp.dirty),
    };
    const response = await updatePriceLevel(modifiedData, successText);
    if (response.status === HTTP_STATUSES.OK) {
      getPriceLevels();
    }
  };

  const handleReorderPriceLevels = async (
    direction: "left" | "right",
    idx: number
  ) => {
    const to = direction === "left" ? idx - 1 : idx + 1;
    const data = [
      { id: priceLevels[idx].id, displayOrder: to },
      { id: priceLevels[to].id, displayOrder: idx },
    ];
    const response = await reorderPriceLevels(data);
    if (response.status === HTTP_STATUSES.OK) {
      getPriceLevels();
    }
  };

  const handleDuplicatePriceLevel = async (
    data: Partial<IPriceLevelPayload>
  ) => {
    const response = await duplicatePriceLevel(data);
    if (response.status === HTTP_STATUSES.OK) {
      await getPriceLevels();
      return response.data;
    }
    return null;
  };

  const handleDeletePriceLevel = async (data: Partial<IPriceLevelPayload>) => {
    const options = await getDeletePriceLevelOptions(data);
    await executeApiRequest({
      options,
      callback: (res: { status: number }) => {
        if (res.status === HTTP_STATUSES.OK) {
          getPriceLevels();
        }
      },
      noInitialErrorMessage: true,
      modalDescription:
        "Provide a reason for this deletion. A manager must enter their pin below to approve and continue.",
      confirmBtnLabel: "Approve",
      successToastMessageForManagerApproval: {
        text: `Price Level ${data.name} deleted successfully.`,
      },
    });
  };

  return {
    isLoading: isCategoriesLoading || isDropTypesLoading || isPriceLevelLoading,
    categories,
    dropTypes,
    categoryDrops,
    priceLevels,
    getCategories,
    getDropTypes,
    getCategoryDrops,
    getPriceLevels,
    handleCreateCategoryDrop,
    handleDeleteCategoryDrop,
    handleCreatePriceLevel,
    handleUpdatePriceLevel,
    handleReorderPriceLevels,
    handleDuplicatePriceLevel,
    handleDeletePriceLevel,
  };
};

interface usePricingMatrixViewModelProps {
  reset?: UseFormReset<IPriceLevelPayload>;
}
